import { parseISO } from 'date-fns';

import { Service } from 'common/src/types/enums/Service.enum';
import { WorkOrderPriority } from 'common/src/types/enums/WorkOrderPriority.enum';
import { WorkOrderStatus } from 'common/src/types/enums/WorkOrderStatus.enum';
import {
  IAccount,
  IEnergyAsset,
  IFacility,
  IUser,
  IWorkOrder,
} from 'common/src/types/interfaces';
import { IMeterList } from 'common/src/types/meter.dtos';

import {
  autoCompleteType,
  datePickerType,
  selectInputType,
  textInputType,
} from '../../../common/forms/FormFieldTypes';

export function defaultValues(entity?: Partial<IWorkOrder>) {
  return {
    title: entity?.title,
    description: entity?.description,
    priority: entity?.priority || WorkOrderPriority.Low,
    status: entity?.status || WorkOrderStatus.Opened,
    dueDate: entity?.dueDate ? parseISO(entity.dueDate) : null,
    assigneeId: entity?.assigneeId,
    serviceTypes: entity?.serviceTypes,
    tags: entity?.tags ?? [],
    facilities: entity?.facilities?.map((facility) => facility.id) ?? [],
    meters: entity?.meters?.map((meter) => meter.id) ?? [],
    energyAssets: entity?.energyAssets?.map((asset) => asset.id) ?? [],
    espmIds: entity?.espmIds ?? [],
    accounts: entity?.accounts?.map((account) => account.id) ?? [],
  };
}

export function getFields(
  accounts: IAccount[] = [],
  energyAssets: IEnergyAsset[] = [],
  facilities: IFacility[] = [],
  meters: IMeterList[] = [],
  users: IUser[] = [],
) {
  const formFieldsConfiguration: Array<
    textInputType | selectInputType | datePickerType | autoCompleteType
  > = [
    {
      name: 'title',
      label: 'Title',
      placeholder: 'Title',
      type: 'textInput',
      required: true,
    },
    {
      name: 'serviceTypes',
      label: 'Service Types',
      placeholder: 'Service Types',
      required: true,
      type: 'selectInput',
      multiple: true,
      values: Object.values(Service).map((item) => ({
        label: item,
        value: item,
      })),
    },
    {
      name: 'status',
      label: 'Status',
      placeholder: 'Status',
      required: true,
      type: 'selectInput',
      values: Object.values(WorkOrderStatus).map((item) => ({
        label: item,
        value: item,
      })),
    },
    {
      name: 'priority',
      label: 'Priority',
      placeholder: 'Priority',
      required: true,
      type: 'selectInput',
      values: Object.values(WorkOrderPriority).map((item) => ({
        label: item,
        value: item,
      })),
    },
    {
      name: 'assigneeId',
      label: 'Assignee',
      type: 'autocomplete',
      required: true,
      placeholder: 'Assignee',
      options: users.map((item) => ({
        label: item.name,
        value: item.id,
        key: item.id,
        disabled: !item.isActive,
      })),
    },
    {
      name: 'description',
      label: 'Description',
      placeholder: 'Description',
      required: true,
      type: 'textInput',
      multiline: true,
    },
    {
      name: 'dueDate',
      label: 'Due Date',
      placeholder: 'Due Date',
      required: false,
      type: 'datePicker',
    },
    {
      clearOnBlur: true,
      freeSolo: true,
      label: 'Tags',
      multiple: true,
      name: 'tags',
      options: [],
      placeholder: 'Tags',
      required: false,
      type: 'autocomplete',
    },
    {
      label: 'Affected Accounts',
      multiple: true,
      name: 'accounts',
      options: accounts.map((item) => ({
        label: item.number,
        value: item.id,
        key: item.id,
        disabled: !item.isActive,
      })),
      placeholder: 'Affected Accounts',
      required: false,
      type: 'autocomplete',
    },
    {
      label: 'Affected Energy Assets',
      multiple: true,
      name: 'energyAssets',
      options: energyAssets.map((item) => ({
        label: item.name,
        value: item.id,
        key: item.id,
      })),
      placeholder: 'Affected Energy Assets',
      required: false,
      type: 'autocomplete',
    },
    {
      label: 'Affected Facilities',
      multiple: true,
      name: 'facilities',
      options: facilities.map((item) => ({
        label: item.name,
        value: item.id,
        key: item.id,
        disabled: !item.isActive,
      })),
      placeholder: 'Affected Facilities',
      required: false,
      type: 'autocomplete',
    },
    {
      placeholder: 'Affected Meters',
      label: 'Affected Meters',
      name: 'meters',
      type: 'autocomplete',
      required: false,
      multiple: true,
      options: meters.map((item) => ({
        label: item.name,
        value: item.id,
        key: item.id,
        disabled: !item.isActive,
      })),
    },
    {
      freeSolo: true,
      clearOnBlur: true,
      label: 'ESPM IDs',
      multiple: true,
      name: 'espmIds',
      options: [],
      placeholder: 'ESPM IDs',
      required: false,
      type: 'autocomplete',
    },
  ];

  return formFieldsConfiguration;
}
